import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ClipboardPen, Inbox, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { JenisTag, StatusBadge } from "@/components/admin/JenisTag";
import { PageHeading } from "@/components/PageHeading";
import { api, formatApiError } from "@/lib/api";
import { formatDateID } from "@/lib/format";

const ComplaintCard = ({ c, index }) => (
  <li className="fade-up" style={{ animationDelay: `${index * 50}ms` }}>
    <Link to={`/pengaduan-saya/${c.id}`} className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md" data-testid={`my-complaint-${c.id}`}>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-bold text-emerald-700">#{c.ticket}</span>
          <StatusBadge status={c.status} />
        </div>
        <JenisTag jenis={c.jenis} />
        <p className="line-clamp-2 text-sm text-slate-600">{c.detail}</p>
        <p className="text-xs text-slate-400">Dikirim {formatDateID(c.tanggal, "d MMMM yyyy")} · Diperbarui {formatDateID(c.updated_at, "d MMM yyyy, HH:mm")}</p>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-emerald-600" />
    </Link>
  </li>
);

export default function MyComplaintsPage() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/my/complaints")
      .then((res) => setItems(res.data))
      .catch((err) => {
        toast.error(formatApiError(err, "Gagal memuat pengaduan"));
        setItems([]);
      });
  }, []);

  return (
    <main className="mx-auto max-w-4xl space-y-8 px-4 py-8 sm:px-6" data-testid="my-complaints-page">
      <PageHeading
        eyebrow="Akun Warga"
        title="Pengaduan Saya"
        description="Pantau progres penyelesaian dan balas tanggapan pengurus RT."
        testId="my-complaints-title"
        action={
          <Button asChild className="rounded-xl bg-emerald-600 hover:bg-emerald-700">
            <Link to="/" data-testid="new-complaint-link"><ClipboardPen className="h-4 w-4" /> Buat Pengaduan</Link>
          </Button>
        }
      />
      {items === null ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500"><LoaderCircle className="h-5 w-5 animate-spin text-emerald-600" /> Memuat pengaduan...</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="my-complaints-empty">
          <Inbox className="h-8 w-8 text-slate-300" />
          <p className="font-heading font-semibold text-slate-800">Belum ada pengaduan</p>
          <p className="text-sm text-slate-500">Pengaduan yang Anda kirim akan tampil di sini.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="my-complaints-list">
          {items.map((c, i) => <ComplaintCard key={c.id} c={c} index={i} />)}
        </ul>
      )}
    </main>
  );
}
